<template>
  <div class="container mt-4">
    <div class="row justify-content-center">
      <div class="col-12 col-lg-10">
        <div class="card">
          <div class="card-body">
            <information v-if="isInformationModalVisible">
              <template slot="header">
                <div class="alert alert-warning d-flex align-items-center justify-content-center text-center" role="alert">
                  <div>
                    <strong>Thanks for registering to invest in communities with us!
                        For security purposes, additional information is required to verify your identity.</strong>
                  </div>
                </div>
              </template>
              <template slot="body">
                <div class="row">
                  <div class="col-md-12 text-center">
                    <button class="btn btn-primary btn-lg btn-accept" @click="beginKba">Start questionnaire</button>
                  </div>
                </div>
              </template>
            </information>
            <h3 class="card-title mb-3">Identity Verification</h3>

            <div v-if="errorMessage" class="alert alert-danger" role="alert">
              {{ errorMessage }}
            </div>

            <div v-if="successMessage" class="alert alert-success" role="alert">
              {{ successMessage }}
            </div>

            <div class="d-flex align-items-center justify-content-start mb-3">
              <div>
                <small class="text-muted">About</small>
                <div class="h5 friendly-timer"><span class="badge badge-info">{{ formattedTime }}</span></div>
              </div>
            </div>

            <dismissable-alert
              class="mb-3"
              :isShowing="true"
              :dismissible="false"
              alertType="success"
              :showIcon="true"
              icon="information"
              title="About these questions"
              subtitle="We do not store your questions or answers. They are provided by our verification partner from publicly available government records."
            />

            <div v-if="loading" class="text-center my-4">
              <div class="spinner-border text-info" role="status" aria-hidden="true"></div>
              <div class="small text-muted mt-2">Loading questions…</div>
            </div>

            <form v-if="questions.length" @submit.prevent="onSubmit">
              <div
                v-for="(q, qi) in questions"
                :key="q.id"
                class="card mb-4 question-card shadow-sm"
                :aria-labelledby="`q-${q.id}-label`"
              >
                <div class="card-header d-flex justify-content-between align-items-center">
                  <strong>Question {{ qi + 1 }}</strong>
                  <small class="text-muted">Select one</small>
                </div>
                <div class="card-body">
                  <div class="question-text mb-3" :id="`q-${q.id}-label`">{{ q.text }}</div>
                  <div class="answers">
                    <div v-for="ans in q.answers" :key="ans.id" class="answer-item mb-2 p-2 border rounded">
                      <div class="custom-control custom-radio">
                        <input
                          class="custom-control-input"
                          type="radio"
                          :name="`q-${q.id}`"
                          :id="`q-${q.id}-a-${ans.id}`"
                          :value="ans.id"
                          v-model="selectedAnswers[q.id]"
                          :disabled="submitting || loading || expired"
                        />
                        <label class="custom-control-label" :for="`q-${q.id}-a-${ans.id}`">
                          {{ ans.text }}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="d-flex align-items-center mt-3">
                <button
                  type="submit"
                  class="btn btn-primary"
                  :disabled="!isComplete || submitting || expired"
                >
                  Submit Answers
                </button>
                <span v-if="expired" class="text-danger ml-3">Session expired.</span>
              </div>
            </form>


          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState, mapMutations } from 'vuex';
import DismissableAlert from '@/components/widgets/DismissableAlert';
import Information from '@/components/modals/Information';
import { SHOW_INFORMATION_MODAL, HIDE_INFORMATION_MODAL } from '@/store/mutation-types';

export default {
  name: 'KbaVerification',
  components: {
    'dismissable-alert': DismissableAlert,
    information: Information,
  },
  data() {
    return {
      localSessionId: null,
      timerInterval: null,
      now: Date.now(),
      selectedAnswers: {},
      submitting: false,
      successMessage: '',
      errorMessage: '',
      submissionResult: null,
      introMessage: 'In order to securely and instantly verify your identity, you will next be asked a short set of multiple-choice questions that only you should know. These are sourced from publicly available government records, and Calvert Impact Capital never sees or records these questions or answers. If you do not answer the questions correctly in 2 minutes to be instantly verified, you will be prompted to upload a color picture/scan of your government ID that will be reviewed in 1-2 business days before you can proceed.',
    };
  },
  computed: {
    ...mapState('kba', ['questions', 'expiresAt', 'loading', 'error', 'sessionId']),
    ...mapState('home', ['userStatus']),
    ...mapState('app', ['user', 'activeAccount']),
    ...mapState('information', ['isInformationModalVisible']),
    effectiveSessionId() {
      return this.localSessionId || this.sessionId;
    },
    deadlineMs() {
      return this.expiresAt ? new Date(this.expiresAt).getTime() : null;
    },
    remainingMs() {
      if (!this.deadlineMs) return 0;
      const diff = this.deadlineMs - this.now;
      return diff > 0 ? diff : 0;
    },
    minutes() {
      return Math.floor(this.remainingMs / 60000);
    },
    seconds() {
      return Math.floor((this.remainingMs % 60000) / 1000);
    },
    formattedTime() {
      const mm = String(this.minutes).padStart(2, '0');
      const ss = String(this.seconds).padStart(2, '0');
      return `${mm}:${ss}`;
    },
    expired() {
      return this.remainingMs <= 0 && !!this.expiresAt;
    },
    timeRemainingClass() {
      return '';
    },
    isComplete() {
      if (!this.questions.length) return false;
      return this.questions.every(q => this.selectedAnswers[q.id]);
    },
  },
  methods: {
    ...mapActions('kba', ['initiate', 'getQuestions', 'submitAnswers']),
    ...mapActions('home', ['getUserStatus']),
    ...mapMutations('information', { showInformationModal: SHOW_INFORMATION_MODAL, hideInformationModal: HIDE_INFORMATION_MODAL }),
    beginKba() {
      this.hideInformationModal();
      this.start();
    },
    tick() {
      this.now = Date.now();
      if (this.expired) {
        this.clearTimer();
      }
    },
    clearTimer() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }
    },
    async start() {
      try {
        this.successMessage = '';
        this.errorMessage = '';
        this.selectedAnswers = {};
        this.clearTimer();

        // Determine customerId from Vuex user
        const currentUser = this.user || {};
        const customerId = currentUser.dwollaCustomerId
          || (currentUser.accounts && currentUser.accounts[0]
            && currentUser.accounts[0].dwollaCustomerId)
          || (this.activeAccount && this.activeAccount.dwollaCustomerId)
          || null;
        if (!customerId) {
          this.errorMessage = 'Dwolla customer is not eligible for KBA at this time.';
          return;
        }

        const sessionId = await this.initiate({ dwollaCustomerId: customerId });
        this.localSessionId = sessionId;
        const { expiresAt } = await this.getQuestions({ sessionId });
        if (expiresAt) {
          this.now = Date.now();
          this.timerInterval = setInterval(this.tick, 250);
        }
      } catch (e) {
        const msg = (e && (e.error || e.message)) || 'Failed to start KBA session.';
        this.errorMessage = msg;
      }
    },
    async onSubmit() {
      if (!this.isComplete || this.submitting || this.expired) return;
      this.submitting = true;
      this.errorMessage = '';
      try {
        const answers = this.questions.map(q => ({
          questionId: String(q.id),
          answerId: String(this.selectedAnswers[q.id]),
        }));
        const result = await this.submitAnswers({ sessionId: this.effectiveSessionId, answers });
        this.submissionResult = result || {};
        this.clearTimer();
        this.$router.push({ name: 'AccountSummary' });
        return;
      } catch (e) {
        const msg = (e && (e.error || e.message)) || 'Failed to submit answers.';
        this.errorMessage = msg;
      } finally {
        this.submitting = false;
      }
    },
    // restart removed - single-attempt flow
  },
  async mounted() {
    try {
      if (!this.activeAccount || !this.activeAccount.id) {
        // If we cannot determine account, show the intro and let user start manually
        this.showInformationModal(this.introMessage);
        return;
      }
      const status = await this.getUserStatus({ accountId: this.activeAccount.id });
      if ((status && status.status) !== 'kba') {
        this.$router.push({ name: 'AccountSummary' });
        return;
      }
      // Do not auto-start; show intro modal and start only when the user clicks the button
      this.showInformationModal(this.introMessage);
    } catch (e) {
      this.errorMessage = (e && (e.error || e.message)) || 'Unable to load status.';
    }
  },
  beforeDestroy() {
    this.clearTimer();
  },
};

</script>

<style scoped>
.text-success { color: #28a745; }
.text-warning { color: #ffc107; }
.text-danger { color: #dc3545; }
.question-card { border-color: #e9ecef; border-radius: 6px; }
.question-text { font-weight: 500; }
.answers .custom-control { padding-left: 2rem; }
.answer-item { background: #fafbfc; }
.friendly-timer .badge-info {
  background-color: #17a2b8;
}
</style>





// WEBPACK FOOTER //
// KbaVerification.vue?1b671d99